import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';

/**
 * Catches a render-time exception and shows it, instead of letting React unmount the whole tree.
 *
 * WHY THIS EXISTS: this application had no error boundary at all, so a single component throwing
 * during render blanked the entire page — no sidebar, no topbar, no message, and the URL still
 * pointing at the route that failed. That is indistinguishable from "the page is empty", which
 * sends you looking at permissions and data when the real cause is a crash three components deep.
 *
 * The error is shown on screen deliberately. This is an internal ERP, not a public site; the person
 * looking at a broken screen is the person who can report or fix it, and hiding the message from
 * them helps nobody.
 */
interface Props { children: React.ReactNode }
interface State { error: Error | null; info: string | null }

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the component stack — it names the component that actually threw, which the message
    // on its own usually does not.
    this.setState({ info: info.componentStack ?? null });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render failed:', error, info.componentStack);
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <Box sx={{ p: 3 }}>
        <Paper variant="outlined" sx={{ p: 3, borderColor: 'error.main' }}>
          <Typography variant="h6" fontWeight={800} color="error.main" gutterBottom>
            This screen failed to load
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The error below is the actual cause. Everything else in the application still works —
            use the sidebar to navigate away, or reload once the cause is fixed.
          </Typography>

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="body2" fontFamily="monospace" fontWeight={700}>
              {error.name}: {error.message}
            </Typography>
          </Paper>

          {/* The JS stack names the LINE that threw. The component stack below only names the
              component it happened inside, which is rarely enough to fix anything. */}
          {error.stack && (
            <>
              <Typography variant="caption" color="text.secondary">Where it threw</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0, mb: 2, p: 1.5, maxHeight: 220, overflow: 'auto', fontSize: 11,
                  bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap',
                }}
              >
                {error.stack}
              </Box>
            </>
          )}

          {info && (
            <>
              <Typography variant="caption" color="text.secondary">Component stack</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0, p: 1.5, maxHeight: 200, overflow: 'auto', fontSize: 11,
                  bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap',
                }}
              >
                {info.trim()}
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={() => this.setState({ error: null, info: null })}>
              Try again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
